import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';

interface ReviewFormProps {
  tourId: string;
  onSubmit: (reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }) => void;
  onCancel: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ tourId, onSubmit, onCancel }) => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const [userName, setUserName] = useState('');
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      showWarning('Please select a rating', 'Incomplete Form');
      return;
    }
    if (!userName.trim() || !title.trim() || !comment.trim()) {
      showWarning('Please fill in all fields', 'Incomplete Form');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        userName: userName.trim(),
        rating,
        title: title.trim(),
        comment: comment.trim(),
      });
      // Reset form
      setUserName('');
      setRating(0);
      setTitle('');
      setComment('');
    } catch (error) {
      console.error('Error submitting review:', error);
      showError('Failed to submit review. Please try again.', 'Submission Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
      <h4 className="text-lg font-semibold text-primary-900 mb-4">Write a Review</h4>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="userName" className="block text-sm font-medium text-neutral-700 mb-1">
            Your Name
          </label>
          <input
            type="text"
            id="userName"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter your name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Rating
          </label>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="focus:outline-none"
              >
                {star <= rating ? (
                  <StarIcon className="h-6 w-6 text-yellow-400" />
                ) : (
                  <StarOutlineIcon className="h-6 w-6 text-neutral-300" />
                )}
              </button>
            ))}
            <span className="ml-2 text-sm text-neutral-600">
              {rating > 0 && `${rating} out of 5`}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-1">
            Review Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Give your review a title"
            required
          />
        </div>

        <div>
          <label htmlFor="comment" className="block text-sm font-medium text-neutral-700 mb-1">
            Your Review
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Share your experience with this safari..."
            required
          />
        </div>

        <div className="flex space-x-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary-800 hover:bg-primary-700 disabled:bg-neutral-400 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-neutral-200 hover:bg-neutral-300 text-neutral-800 px-4 py-2 rounded-md font-medium transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
